import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    FaBars,
    FaBolt,
    FaCalculator,
    FaChartBar,
    FaChevronDown,
    FaCrown,
    FaEnvelope,
    FaExchangeAlt,
    FaFileImport,
    FaGift,
    FaGraduationCap,
    FaStar,
    FaTimes,
    FaWallet,
    FaWhatsapp,
} from 'react-icons/fa';

import logo from '../../assets/img/logo.png';
import heroDashboardScreen from '../../assets/img/hero-dashboard-screen.png';

import {
    MarketingPage,
    HeaderShell,
    HeaderBar,
    HeaderNavLinks,
    HeaderNavLink,
    HeaderActions,
    HeaderMenuButton,
    MobileMenu,
    MobileMenuLink,
    NavDesktopOnly,
    NavMobileOnly,
    MarketingSection,
    SectionInner,
    SectionHeader,
    SectionTitle,
    SectionSubtitle,
    HeroWrap,
    HeroContent,
    HeroBadge,
    HeroTitle,
    HeroGradient,
    HeroSubtitle,
    HeroActions,
    HeroNote,
    HeroStats,
    HeroStat,
    HeroVisual,
    HeroVisualCard,
    HeroOrb,
    FeaturesGrid,
    FeatureCard,
    FeatureIconBox,
    FeatureTitle,
    FeatureDesc,
    StepsGrid,
    StepCard,
    StepNumber,
    StepTitle,
    StepDesc,
    PlanCardRoot,
    PlanBadgesRow,
    PlanBadge,
    PlanCardBody,
    PlanIconBox,
    PlanName,
    PlanDescription,
    PlanPriceBlock,
    PlanPrice,
    PlanPriceSub,
    TestimonialCard,
    TestimonialStars,
    TestimonialQuote,
    TestimonialAuthor,
    TestimonialAvatar,
    TestimonialInfo,
    FaqList,
    FaqItem,
    FaqQuestion,
    FaqAnswer,
    ContactCards,
    ContactCard,
    ContactHighlight,
    ContactIcon,
    ContactInfo,
    ContactLabel,
    ContactValue,
    MarketingLinkButton,
    MarketingClosing,
    CtaBanner,
    CtaBannerInner,
    CtaBannerActions,
    MarketingFooterWrap,
    FooterGlow,
    FooterCard,
    FooterGrid,
    FooterBrand,
    FooterColumn,
    FooterLinkList,
    FooterBottom,
    FooterCopy,
    FooterBottomLinks,
    LogoLink,
    HeroScreen,
    VideoFrame,
    AboutCard,
    ThreeColumnPlansGrid,
    TwoColumnTestimonialsGrid,
} from './marketingTheme';

const WHATSAPP_URL = 'https://wa.me/38999361310';
const WHATSAPP_DISPLAY = '(38) 99936-1310';
const CONTACT_EMAIL = 'app.gbinary@gmail.com';

/** Botão-link interno (react-router) com o visual dos botões da landing */
function NavButton({ to, children, ...props }) {
    return (
        <MarketingLinkButton as={Link} to={to} {...props}>
            {children}
        </MarketingLinkButton>
    );
}

function ExternalButton({ href, children, ...props }) {
    return (
        <MarketingLinkButton href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
        </MarketingLinkButton>
    );
}

export default function Home() {
    const { t } = useTranslation();
    const [logged, setLogged] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [openFaq, setOpenFaq] = useState(1);

    useEffect(() => {
        const el = document.getElementById('containerLayout');
        if (el) el.style.height = 'auto';
        setLogged(!!localStorage.getItem('@user_token'));

        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const logout = () => {
        localStorage.clear();
        window.location = '/';
    };

    const closeMenu = () => setMenuOpen(false);
    const startUrl = logged ? '/dashboard' : '/register';

    const navLinks = [
        { href: '#recursos', label: t('landing_nav_features') },
        { href: '#como-funciona', label: t('landing_nav_how') },
        { href: '#video', label: t('landing_nav_video') },
        { href: '#planos', label: t('landing_nav_plans') },
        { href: '#contato', label: t('landing_nav_contact') },
    ];

    const features = [
        { icon: FaChartBar, n: 1 },
        { icon: FaExchangeAlt, n: 2 },
        { icon: FaGraduationCap, n: 3 },
        { icon: FaWallet, n: 4 },
        { icon: FaCalculator, n: 5 },
        { icon: FaFileImport, n: 6 },
    ];

    const plans = [
        {
            id: 'trial',
            icon: FaGift,
            cta: t('landing_plan_trial_cta'),
            to: startUrl,
        },
        {
            id: 'semiannual',
            icon: FaStar,
            cta: t('landing_plan_cta'),
            to: logged ? '/plans' : '/register',
        },
        {
            id: 'yearly',
            icon: FaCrown,
            popular: true,
            discount: t('landing_plan_yearly_discount'),
            cta: t('landing_plan_cta'),
            to: logged ? '/plans' : '/register',
        },
    ];

    const testimonials = [
        { quote: t('home_feedback_card_1'), name: 'Francisco Nunes' },
        { quote: t('home_feedback_card_2'), name: 'Emerson Santos' },
        { quote: t('home_feedback_card_3'), name: 'Sillas Xavier' },
        { quote: t('home_feedback_card_4'), name: 'Taynara Souza' },
    ];

    const faqs = [1, 2, 3, 4, 5, 6];

    return (
        <MarketingPage id="landing-home">
            <HeaderShell $scrolled={scrolled}>
                <HeaderBar>
                    <LogoLink href="/" aria-label="GBinary" $size={36}>
                        <img src={logo} alt="GBinary" />
                    </LogoLink>

                    <HeaderNavLinks>
                        {navLinks.map((link) => (
                            <HeaderNavLink key={link.href} href={link.href}>
                                {link.label}
                            </HeaderNavLink>
                        ))}
                    </HeaderNavLinks>

                    <HeaderActions>
                        {logged ? (
                            <>
                                <NavDesktopOnly>
                                    <MarketingLinkButton as="button" type="button" $variant="ghost" $size="sm" onClick={logout}>
                                        {t('landing_btn_logout')}
                                    </MarketingLinkButton>
                                </NavDesktopOnly>
                                <NavDesktopOnly>
                                    <NavButton to="/dashboard" $size="sm">{t('landing_btn_dashboard')}</NavButton>
                                </NavDesktopOnly>
                                <NavMobileOnly>
                                    <NavButton to="/dashboard" $size="sm">{t('landing_btn_dashboard_short')}</NavButton>
                                </NavMobileOnly>
                            </>
                        ) : (
                            <>
                                <NavDesktopOnly>
                                    <NavButton to="/login" $variant="ghost" $size="sm">{t('landing_btn_login')}</NavButton>
                                </NavDesktopOnly>
                                <NavDesktopOnly>
                                    <NavButton to="/register" $size="sm">{t('landing_btn_start')}</NavButton>
                                </NavDesktopOnly>
                                <NavMobileOnly>
                                    <NavButton to="/register" $size="sm">{t('landing_btn_start_short')}</NavButton>
                                </NavMobileOnly>
                            </>
                        )}

                        <HeaderMenuButton
                            type="button"
                            aria-label={menuOpen ? t('landing_menu_close') : t('landing_menu_open')}
                            onClick={() => setMenuOpen((open) => !open)}
                        >
                            {menuOpen ? <FaTimes /> : <FaBars />}
                        </HeaderMenuButton>
                    </HeaderActions>
                </HeaderBar>

                <MobileMenu $open={menuOpen}>
                    {navLinks.map((link) => (
                        <MobileMenuLink key={link.href} href={link.href} onClick={closeMenu}>
                            {link.label}
                        </MobileMenuLink>
                    ))}
                    {logged ? (
                        <MobileMenuLink as="button" type="button" onClick={logout} style={{ background: 'none', border: 0, textAlign: 'left' }}>
                            {t('landing_btn_logout')}
                        </MobileMenuLink>
                    ) : (
                        <MobileMenuLink as={Link} to="/login" onClick={closeMenu}>
                            {t('landing_btn_login')}
                        </MobileMenuLink>
                    )}
                </MobileMenu>
            </HeaderShell>

            <main>
                <MarketingSection id="inicio" $tone="dark" $first>
                    <HeroWrap>
                        <HeroContent>
                            <HeroBadge>{t('landing_hero_badge')}</HeroBadge>
                            <HeroTitle>
                                {t('landing_hero_title_1')}
                                <br />
                                <HeroGradient>{t('landing_hero_title_2')}</HeroGradient>
                            </HeroTitle>
                            <HeroSubtitle>{t('landing_hero_subtitle')}</HeroSubtitle>
                            <HeroActions>
                                <NavButton to={startUrl} $size="lg">
                                    <FaBolt aria-hidden />
                                    {logged ? t('landing_btn_dashboard') : t('landing_hero_cta')}
                                </NavButton>
                                <ExternalButton href={WHATSAPP_URL} $variant="secondary" $size="lg">
                                    <FaWhatsapp aria-hidden />
                                    {t('landing_hero_cta_whatsapp')}
                                </ExternalButton>
                            </HeroActions>
                            <HeroNote>{t('landing_hero_note')}</HeroNote>

                            <HeroStats>
                                {[1, 2, 3].map((n) => (
                                    <HeroStat key={n}>
                                        <strong>{t(`landing_hero_stat_${n}_n`)}</strong>
                                        <span>{t(`landing_hero_stat_${n}_l`)}</span>
                                    </HeroStat>
                                ))}
                            </HeroStats>
                        </HeroContent>

                        <HeroVisual>
                            <HeroOrb />
                            <HeroScreen>
                                <img
                                    src={heroDashboardScreen}
                                    alt={t('landing_hero_image_alt')}
                                    width={1024}
                                    height={644}
                                />
                            </HeroScreen>
                            <HeroVisualCard $top="2%" $left="-6%" $delay="0s" aria-hidden>
                                <div className="label">{t('landing_hero_card_1_label')}</div>
                                <div className="value">{t('landing_hero_card_1_value')}</div>
                                <div className="sub">{t('landing_hero_card_1_sub')}</div>
                            </HeroVisualCard>
                            <HeroVisualCard $top="40%" $left="68%" $delay="1s" aria-hidden>
                                <div className="label">{t('landing_hero_card_2_label')}</div>
                                <div className="value">{t('landing_hero_card_2_value')}</div>
                                <div className="sub">{t('landing_hero_card_2_sub')}</div>
                            </HeroVisualCard>
                            <HeroVisualCard $top="74%" $left="4%" $delay="2s" aria-hidden>
                                <div className="label">{t('landing_hero_card_3_label')}</div>
                                <div className="value">{t('landing_hero_card_3_value')}</div>
                                <div className="sub">{t('landing_hero_card_3_sub')}</div>
                            </HeroVisualCard>
                        </HeroVisual>
                    </HeroWrap>
                </MarketingSection>

                <MarketingSection id="recursos" $tone="light" $noBorder>
                    <SectionInner>
                        <SectionHeader>
                            <SectionTitle>{t('landing_features_title')}</SectionTitle>
                            <SectionSubtitle>{t('landing_features_subtitle')}</SectionSubtitle>
                        </SectionHeader>
                        <FeaturesGrid>
                            {features.map(({ icon: Icon, n }) => (
                                <FeatureCard key={n}>
                                    <FeatureIconBox>
                                        <Icon aria-hidden />
                                    </FeatureIconBox>
                                    <FeatureTitle>{t(`landing_feature_${n}_title`)}</FeatureTitle>
                                    <FeatureDesc>{t(`landing_feature_${n}_desc`)}</FeatureDesc>
                                </FeatureCard>
                            ))}
                        </FeaturesGrid>
                    </SectionInner>
                </MarketingSection>

                <MarketingSection id="como-funciona" $tone="muted">
                    <SectionInner>
                        <SectionHeader>
                            <SectionTitle>{t('landing_how_title')}</SectionTitle>
                            <SectionSubtitle>{t('landing_how_subtitle')}</SectionSubtitle>
                        </SectionHeader>
                        <StepsGrid>
                            {[1, 2, 3].map((n) => (
                                <StepCard key={n}>
                                    <StepNumber>{`0${n}`}</StepNumber>
                                    <StepTitle>{t(`landing_how_${n}_title`)}</StepTitle>
                                    <StepDesc>{t(`landing_how_${n}_desc`)}</StepDesc>
                                </StepCard>
                            ))}
                        </StepsGrid>
                    </SectionInner>
                </MarketingSection>

                <MarketingSection id="video" $tone="dark">
                    <SectionInner>
                        <SectionHeader>
                            <SectionTitle>{t('landing_video_title')}</SectionTitle>
                            <SectionSubtitle>{t('landing_video_subtitle')}</SectionSubtitle>
                        </SectionHeader>
                        <VideoFrame>
                            <iframe
                                title="GBinary — tutorial"
                                src="https://www.youtube.com/embed/JbRVo26uUsI"
                                loading="lazy"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                            />
                        </VideoFrame>
                    </SectionInner>
                </MarketingSection>

                <MarketingSection id="planos" $tone="muted" $noBorder>
                    <SectionInner $narrow>
                        <SectionHeader>
                            <SectionTitle>{t('landing_plans_title')}</SectionTitle>
                            <SectionSubtitle>{t('landing_plans_subtitle')}</SectionSubtitle>
                        </SectionHeader>
                        <ThreeColumnPlansGrid>
                            {plans.map((plan) => {
                                const Icon = plan.icon;
                                return (
                                    <PlanCardRoot key={plan.id}>
                                        {(plan.popular || plan.discount) && (
                                            <PlanBadgesRow>
                                                {plan.popular && <PlanBadge $variant="popular">{t('landing_plan_popular')}</PlanBadge>}
                                                {plan.discount && <PlanBadge $variant="discount">{plan.discount}</PlanBadge>}
                                            </PlanBadgesRow>
                                        )}
                                        <PlanCardBody>
                                            <PlanIconBox>
                                                <Icon aria-hidden />
                                            </PlanIconBox>
                                            <PlanName>{t(`landing_plan_${plan.id}_name`)}</PlanName>
                                            <PlanDescription>{t(`landing_plan_${plan.id}_desc`)}</PlanDescription>
                                            <PlanPriceBlock>
                                                <PlanPrice>{t(`landing_plan_${plan.id}_price`)}</PlanPrice>
                                                <PlanPriceSub>{t(`landing_plan_${plan.id}_sub`)}</PlanPriceSub>
                                            </PlanPriceBlock>
                                        </PlanCardBody>
                                        <NavButton
                                            to={plan.to}
                                            $variant={plan.popular ? undefined : 'outline'}
                                            $size="sm"
                                            style={{ marginTop: 16, width: '100%' }}
                                        >
                                            {plan.cta}
                                        </NavButton>
                                    </PlanCardRoot>
                                );
                            })}
                        </ThreeColumnPlansGrid>
                    </SectionInner>
                </MarketingSection>

                <MarketingSection id="depoimentos" $tone="light">
                    <SectionInner $narrow>
                        <SectionHeader>
                            <SectionTitle>{t('landing_testimonials_title')}</SectionTitle>
                            <SectionSubtitle>{t('landing_testimonials_subtitle')}</SectionSubtitle>
                        </SectionHeader>
                        <TwoColumnTestimonialsGrid>
                            {testimonials.map((item) => (
                                <TestimonialCard key={item.name}>
                                    <TestimonialStars aria-label="5/5">★★★★★</TestimonialStars>
                                    <TestimonialQuote>&ldquo;{item.quote}&rdquo;</TestimonialQuote>
                                    <TestimonialAuthor>
                                        <TestimonialAvatar aria-hidden>
                                            {item.name.split(' ').map((part) => part[0]).join('')}
                                        </TestimonialAvatar>
                                        <TestimonialInfo>
                                            <strong>{item.name}</strong>
                                            <span>{t('landing_testimonials_role')}</span>
                                        </TestimonialInfo>
                                    </TestimonialAuthor>
                                </TestimonialCard>
                            ))}
                        </TwoColumnTestimonialsGrid>
                    </SectionInner>
                </MarketingSection>

                <MarketingSection id="sobre" $tone="dark">
                    <SectionInner $narrow>
                        <SectionHeader>
                            <SectionTitle>{t('landing_about_title')}</SectionTitle>
                        </SectionHeader>
                        <AboutCard>
                            <p>{t('home_landing_about_p1')}</p>
                            <p>{t('home_landing_about_p2')}</p>
                            <p>{t('home_landing_about_p3')}</p>
                            <p>{t('home_landing_about_p4')}</p>
                        </AboutCard>
                    </SectionInner>
                </MarketingSection>

                <MarketingSection id="faq" $tone="light" $noBorder>
                    <SectionInner $narrow>
                        <SectionHeader>
                            <SectionTitle>{t('landing_faq_title')}</SectionTitle>
                            <SectionSubtitle>{t('landing_faq_subtitle')}</SectionSubtitle>
                        </SectionHeader>
                        <FaqList>
                            {faqs.map((n) => {
                                const isOpen = openFaq === n;
                                return (
                                    <FaqItem key={n} $open={isOpen}>
                                        <FaqQuestion
                                            type="button"
                                            aria-expanded={isOpen}
                                            onClick={() => setOpenFaq((current) => (current === n ? null : n))}
                                        >
                                            {t(`landing_faq_${n}_q`)}
                                            <FaChevronDown aria-hidden />
                                        </FaqQuestion>
                                        <FaqAnswer $open={isOpen}>{t(`landing_faq_${n}_a`)}</FaqAnswer>
                                    </FaqItem>
                                );
                            })}
                        </FaqList>
                    </SectionInner>
                </MarketingSection>

                <MarketingSection id="contato" $tone="light">
                    <SectionInner $narrow>
                        <SectionHeader>
                            <SectionTitle>{t('landing_contact_title')}</SectionTitle>
                            <SectionSubtitle>{t('landing_contact_subtitle')}</SectionSubtitle>
                        </SectionHeader>
                        <ContactCards>
                            <ContactCard $featured>
                                <ContactHighlight>
                                    <ContactIcon $variant="whatsapp" aria-hidden>
                                        <FaWhatsapp />
                                    </ContactIcon>
                                    <ContactInfo>
                                        <ContactLabel>{t('landing_contact_whatsapp_label')}</ContactLabel>
                                        <ContactValue>{WHATSAPP_DISPLAY}</ContactValue>
                                        <p>{t('landing_contact_whatsapp_time')}</p>
                                    </ContactInfo>
                                </ContactHighlight>
                                <ExternalButton href={WHATSAPP_URL} $size="lg" $variant="whatsapp">
                                    <FaWhatsapp aria-hidden />
                                    {t('landing_contact_whatsapp_cta')}
                                </ExternalButton>
                            </ContactCard>

                            <ContactCard>
                                <ContactHighlight>
                                    <ContactIcon aria-hidden>
                                        <FaEnvelope />
                                    </ContactIcon>
                                    <ContactInfo>
                                        <ContactLabel>{t('landing_contact_email_label')}</ContactLabel>
                                        <ContactValue>{CONTACT_EMAIL}</ContactValue>
                                        <p>{t('landing_contact_email_desc')}</p>
                                    </ContactInfo>
                                </ContactHighlight>
                                <MarketingLinkButton href={`mailto:${CONTACT_EMAIL}`} $size="lg">
                                    <FaEnvelope aria-hidden />
                                    {t('landing_contact_email_cta')}
                                </MarketingLinkButton>
                            </ContactCard>
                        </ContactCards>
                    </SectionInner>
                </MarketingSection>
            </main>

            <MarketingClosing>
                <CtaBanner>
                    <CtaBannerInner>
                        <h2>{t('landing_cta_title')}</h2>
                        <p>{t('landing_cta_subtitle')}</p>
                        <CtaBannerActions>
                            <NavButton to={startUrl} $size="lg">
                                <FaBolt aria-hidden />
                                {logged ? t('landing_btn_dashboard') : t('landing_cta_btn')}
                            </NavButton>
                            <ExternalButton href={WHATSAPP_URL} $variant="secondary" $size="lg">
                                <FaWhatsapp aria-hidden />
                                {t('landing_cta_whatsapp')}
                            </ExternalButton>
                        </CtaBannerActions>
                    </CtaBannerInner>
                </CtaBanner>

                <MarketingFooterWrap>
                    <FooterGlow />
                    <FooterCard>
                        <FooterGrid>
                            <FooterBrand>
                                <LogoLink href="/" aria-label="GBinary" $size={40}>
                                    <img src={logo} alt="GBinary" />
                                </LogoLink>
                                <p>{t('landing_footer_desc')}</p>
                            </FooterBrand>

                            <FooterColumn>
                                <h4>{t('landing_footer_product')}</h4>
                                <FooterLinkList>
                                    <li><a href="#recursos">{t('landing_nav_features')}</a></li>
                                    <li><a href="#como-funciona">{t('landing_nav_how')}</a></li>
                                    <li><a href="#planos">{t('landing_footer_plans')}</a></li>
                                    <li><Link to="/policy-privacy">{t('landing_footer_privacy')}</Link></li>
                                </FooterLinkList>
                            </FooterColumn>

                            <FooterColumn>
                                <h4>{t('landing_footer_contact')}</h4>
                                <FooterLinkList>
                                    <li>
                                        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                                            WhatsApp {WHATSAPP_DISPLAY}
                                        </a>
                                    </li>
                                    <li><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></li>
                                    <li><a href="#faq">{t('landing_footer_faq')}</a></li>
                                </FooterLinkList>
                            </FooterColumn>

                            <FooterColumn>
                                <h4>{t('landing_footer_account')}</h4>
                                <FooterLinkList>
                                    <li><Link to="/login">{t('landing_btn_login')}</Link></li>
                                    <li><Link to="/register">{t('landing_footer_register')}</Link></li>
                                    <li><Link to="/register">{t('landing_footer_trial')}</Link></li>
                                </FooterLinkList>
                            </FooterColumn>
                        </FooterGrid>
                    </FooterCard>

                    <FooterBottom>
                        <FooterCopy>{t('landing_footer_copy', { year: new Date().getFullYear() })}</FooterCopy>
                        <FooterBottomLinks>
                            <Link to="/policy-privacy">{t('landing_footer_privacy_short')}</Link>
                            <a href="#contato">{t('landing_nav_contact')}</a>
                            <a href="#planos">{t('landing_nav_plans')}</a>
                        </FooterBottomLinks>
                    </FooterBottom>
                </MarketingFooterWrap>
            </MarketingClosing>
        </MarketingPage>
    );
}
